import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { apiClient } from '../api/client'
import { Eye, EyeOff, Mail, Lock, User, Phone, Check } from 'lucide-react'

export function RegisterPage() {
  const { t } = useTranslation()
  const { register } = useAuth()
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    name: '',
    phone: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Phone verification state
  const [verificationCode, setVerificationCode] = useState('')
  const [isCodeSent, setIsCodeSent] = useState(false)
  const [isPhoneVerified, setIsPhoneVerified] = useState(false)
  const [isSendingCode, setIsSendingCode] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [countdown, setCountdown] = useState(0)

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (field === 'phone') {
      setIsPhoneVerified(false)
      setIsCodeSent(false)
    }
  }

  const formatPhoneInput = (value: string): string => {
    const numbers = value.replace(/\D/g, '')
    if (numbers.length <= 3) return numbers
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneInput(e.target.value)
    handleChange('phone', formatted)
  }

  const handleSendCode = async () => {
    if (!formData.phone || formData.phone.replace(/\D/g, '').length < 10) {
      setError(t('auth.invalidPhone'))
      return
    }

    setIsSendingCode(true)
    setError('')

    try {
      const result = await apiClient.sendVerificationCode({ phone: formData.phone })
      setIsCodeSent(true)
      setCountdown(result.expiresIn || 180)

      // Start countdown
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code')
    } finally {
      setIsSendingCode(false)
    }
  }

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError(t('auth.codeInvalid'))
      return
    }

    setIsVerifying(true)
    setError('')

    try {
      const verified = await apiClient.verifyPhone({ phone: formData.phone, code: verificationCode })
      if (verified) {
        setIsPhoneVerified(true)
      } else {
        setError(t('auth.codeInvalid'))
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed')
    } finally {
      setIsVerifying(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validation
    if (!isPhoneVerified) {
      setError(t('auth.phoneNotVerified'))
      return
    }

    if (formData.password !== formData.passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다')
      return
    }

    if (formData.password.length < 8) {
      setError('비밀번호는 8자 이상이어야 합니다')
      return
    }

    setIsLoading(true)

    try {
      await register({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        notification_type: 'alimtalk',
      })
      navigate('/', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.registerFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-light-bg dark:bg-dark-bg px-4 py-8">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary">ScreenEagle</h1>
          <p className="text-light-text-secondary dark:text-dark-text-secondary mt-2">
            움직임 모니터링 알람
          </p>
        </div>

        {/* Register Form */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-center mb-6">{t('auth.register')}</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="label">{t('auth.email')}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-text-muted dark:text-dark-text-muted" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="input pl-10"
                  placeholder="email@example.com"
                  required
                />
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="label">{t('auth.name')}</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-text-muted dark:text-dark-text-muted" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="input pl-10"
                  placeholder="홍길동"
                  required
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="label">{t('auth.phone')}</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-text-muted dark:text-dark-text-muted" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    className="input pl-10"
                    placeholder={t('auth.phoneFormat')}
                    maxLength={13}
                    disabled={isPhoneVerified}
                    required
                  />
                  {isPhoneVerified && (
                    <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-success" />
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleSendCode}
                  disabled={isSendingCode || isPhoneVerified || formData.phone.replace(/\D/g, '').length < 10}
                  className="btn btn-secondary whitespace-nowrap"
                >
                  {isSendingCode ? '...' : isCodeSent ? t('auth.resendCode') : t('auth.sendCode')}
                </button>
              </div>
            </div>

            {/* Verification Code */}
            {isCodeSent && !isPhoneVerified && (
              <div>
                <label className="label">
                  {t('auth.verificationCode')}{' '}
                  {countdown > 0 && (
                    <span className="text-warning">
                      ({Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')})
                    </span>
                  )}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="input flex-1"
                    placeholder="000000"
                    maxLength={6}
                  />
                  <button
                    type="button"
                    onClick={handleVerifyCode}
                    disabled={isVerifying || verificationCode.length !== 6}
                    className="btn btn-primary"
                  >
                    {isVerifying ? '...' : t('auth.verifyCode')}
                  </button>
                </div>
              </div>
            )}

            {/* Password */}
            <div>
              <label className="label">{t('auth.password')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-text-muted dark:text-dark-text-muted" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  className="input pl-10 pr-10"
                  placeholder="••••••••"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-light-text-muted dark:text-dark-text-muted hover:text-light-text dark:hover:text-dark-text"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Password Confirm */}
            <div>
              <label className="label">{t('auth.passwordConfirm')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-text-muted dark:text-dark-text-muted" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.passwordConfirm}
                  onChange={(e) => handleChange('passwordConfirm', e.target.value)}
                  className="input pl-10"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="p-3 bg-danger/10 border border-danger/20 rounded-md text-danger text-sm">
                {error}
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading || !isPhoneVerified}
              className="btn btn-primary w-full h-11 flex items-center justify-center"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t('auth.register')
              )}
            </button>
          </form>

          {/* Login link */}
          <div className="mt-6 text-center text-sm text-light-text-secondary dark:text-dark-text-secondary">
            {t('auth.hasAccount')}{' '}
            <Link to="/login" className="text-primary hover:underline">
              {t('auth.login')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
